import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  ClipboardList, AlertOctagon, Clock, ShieldAlert, Download,
} from "lucide-react";
import DateFilter from "../components/layout/DateFilter";
import { PmProductionSummaryCard } from "../components/PmProductionSummaryCard";

const ALL_DEPTS = ["MOLD", "FRAME", "GFRC", "FINISH", "FIBERGLASS"];
const DEPT_COLORS = {
  MOLD: "#00D4FF", FRAME: "#FBBF24", GFRC: "#A78BFA",
  FINISH: "#EC4899", FIBERGLASS: "#34D399",
};
const CAUSE_OPTIONS = [
  "Project Manager Review",
  "Engineering Review",
  "Drafting Review",
];

export default function PmView() {
  const { user } = useAuth();
  const isAdmin = user?.role_name === "Administrator" || user?.role === "admin";
  const plantScope = String(user?.plant_scope || "BOTH").toUpperCase();
  const canSeeSLC = isAdmin || plantScope === "SLC" || plantScope === "BOTH";
  const canSeeMex = isAdmin || plantScope === "MEX" || plantScope === "BOTH";

  const [filter, setFilter] = useState({ preset: "all" });
  const [showMexicoState, setShowMexico] = useState(() => !canSeeSLC && canSeeMex);
  const showMexico = !canSeeMex ? false : (!canSeeSLC ? true : showMexicoState);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedCause, setSelectedCause] = useState("");
  const [selectedJob, setSelectedJob] = useState("");
  const [data, setData] = useState({ rows: [], per_dept: {}, per_cause: {}, options: { all_depts: ALL_DEPTS, all_causes: CAUSE_OPTIONS, jobs: [] } });
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (selectedDepts.length > 0) p.depts = selectedDepts.join(",");
    if (selectedCause)            p.causes = selectedCause;
    if (selectedJob)              p.job = selectedJob;
    return p;
  }, [filter, showMexico, selectedDepts, selectedCause, selectedJob]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/pm-view/ncr-open", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const toggleDept = (d) =>
    setSelectedDepts((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const clearFilters = () => {
    setSelectedDepts([]); setSelectedCause(""); setSelectedJob("");
  };

  const downloadPdf = async () => {
    try {
      setPdfLoading(true);
      const r = await api.get("/pm-view/ncr-open/export", { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `pm_view_open_ncrs_${showMexico ? "mexico" : "slc"}_${stamp}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pm-view-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase flex items-center gap-2" style={{ color: "#EC4899" }}>
            <ClipboardList size={14} /> 05 · PM View
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">Project Manager Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Open NCRs pending review · {showMexico ? "MEXICO" : "SLC"} plant scope
          </p>
        </div>
        {canSeeSLC && canSeeMex ? (
          <button type="button" data-testid="pm-toggle-mexico"
            onClick={() => setShowMexico((v) => !v)}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-mono border transition-colors ${
              showMexico ? "bg-[#FBBF24] text-[#0A0B10] border-[#FBBF24]" : "bg-[#12141D] text-muted-foreground border-border hover:text-white"
            }`}>
            {showMexico ? "Showing Mexico" : "Show Mexico Plants"}
          </button>
        ) : (
          <span className="px-3 py-2 text-[10px] tracking-widest uppercase font-mono border border-border bg-[#0A0B10] text-muted-foreground">
            Plant: {canSeeMex ? "Mexico" : "SLC"} only
          </span>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCard testid="pm-kpi-open"     icon={<AlertOctagon size={16} />} label="Open"     value={loading ? "—" : String(data.total ?? 0)} accent="#EC4899" />
        <KpiCard testid="pm-kpi-breached" icon={<ShieldAlert size={16} />}  label="Breached" value={loading ? "—" : String(data.breached_count ?? 0)} accent="#F87171" />
        <KpiCard testid="pm-kpi-oldest"   icon={<Clock size={16} />}        label="Oldest"   value={loading ? "—" : `${Math.round(data.oldest_hours ?? 0)}h`} accent="#FBBF24" />
      </div>

      {/* Filters + SLA Watch card */}
      <section className="relative bg-[#12141D] border border-border overflow-hidden p-5" data-testid="pm-filters-card">
        <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg,#EC4899,#EC489966)" }} />

        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <AlertOctagon size={18} style={{ color: "#EC4899" }} />
            <h3 className="font-heading font-bold text-xl">Open NCRs · SLA Watch — PM View</h3>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="pm-download-pdf"
              onClick={downloadPdf} disabled={pdfLoading || loading}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-mono px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#EC4899", color: "#0A0B10", borderColor: "#EC4899" }}>
              <Download size={12} /> {pdfLoading ? "Preparing…" : "Download PDF"}
            </button>
            <button type="button" data-testid="pm-clear-filters" onClick={clearFilters}
              className="text-[10px] tracking-widest uppercase font-mono px-2 py-1 border border-border text-muted-foreground hover:text-white transition-colors">
              Clear filters
            </button>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <DateFilter value={filter} onChange={setFilter} />

          {/* Cause dropdown */}
          <div className="inline-flex items-center gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono">Cause</span>
            <select data-testid="pm-cause-filter" value={selectedCause}
              onChange={(e) => setSelectedCause(e.target.value)}
              className="bg-[#0A0B10] border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-white cursor-pointer focus:outline-none focus:border-[#EC4899] transition-colors">
              <option value="">All Review Causes</option>
              {CAUSE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Dept multi-select chips */}
          <div className="inline-flex items-center gap-2" data-testid="pm-dept-filter">
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono">Dept</span>
            <div className="flex flex-wrap gap-1">
              {ALL_DEPTS.map((d) => {
                const active = selectedDepts.includes(d);
                return (
                  <button key={d} type="button" data-testid={`pm-dept-chip-${d.toLowerCase()}`}
                    onClick={() => toggleDept(d)}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-mono border transition-colors ${
                      active ? "text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground border-border hover:text-white"
                    }`}
                    style={active ? { backgroundColor: DEPT_COLORS[d], borderColor: DEPT_COLORS[d] } : {}}>
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Job dropdown */}
          <div className="inline-flex items-center gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono">Job</span>
            <select data-testid="pm-job-filter" value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="bg-[#0A0B10] border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-white cursor-pointer focus:outline-none focus:border-[#EC4899] transition-colors max-w-[220px] truncate">
              <option value="">All Jobs</option>
              {(data.options?.jobs || []).map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
        </div>

        {/* Table (mirrors Open NCRs SLA Watch) */}
        {loading && (
          <div className="py-16 text-center text-sm uppercase tracking-widest text-muted-foreground">Loading…</div>
        )}
        {!loading && (data.rows || []).length === 0 && (
          <div className="py-16 text-center text-sm uppercase tracking-widest text-muted-foreground" data-testid="pm-empty">
            No open NCRs match current filters
          </div>
        )}
        {!loading && (data.rows || []).length > 0 && (
          <div className="overflow-x-auto" data-testid="pm-table-wrap">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Age</th>
                  <th className="py-2 pr-3">Dept</th>
                  <th className="py-2 pr-3">Cause (Status)</th>
                  <th className="py-2 pr-3">Job</th>
                  <th className="py-2 pr-3">Piece</th>
                  <th className="py-2 pr-3">Item</th>
                  <th className="py-2 pr-3">Problem</th>
                  <th className="py-2 pr-3">Created By</th>
                </tr>
              </thead>
              <tbody data-testid="pm-table-body">
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/40 hover:bg-[#0A0B10]/40 transition-colors"
                      data-testid={`pm-row-${i}`}>
                    <td className="py-2 pr-3 font-mono text-right">
                      <span className={r.breached ? "text-red-400 font-semibold" : "text-white/80"}>
                        {Math.round(r.age_hours || 0)}h
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-mono"
                            style={{ color: DEPT_COLORS[r._dept] }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: DEPT_COLORS[r._dept] }} />
                        {r._dept}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{r.status}</td>
                    <td className="py-2 pr-3">{r.job}</td>
                    <td className="py-2 pr-3 font-mono">
                      <div>{r.piece_mark || "—"}</div>
                      {r.piece_sn && (
                        <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`pm-row-${i}-piece-sn`}>
                          SN: {r.piece_sn}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.item || "—"}</td>
                    <td className="py-2 pr-3">{r.problem || "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.created_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Summary chips */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard testid="pm-summary-dept" title="By Department"
          entries={ALL_DEPTS.map((d) => [d, data.per_dept?.[d] || 0])}
          colorMap={DEPT_COLORS} />
        <SummaryCard testid="pm-summary-cause" title="By Cause"
          entries={CAUSE_OPTIONS.map((c) => [c, data.per_cause?.[c] || 0])}
          colorMap={{ "Project Manager Review": "#EC4899", "Engineering Review": "#00D4FF", "Drafting Review": "#A78BFA" }} />
      </div>

      <PmProductionSummaryCard showMexico={showMexico} preferredJob={selectedJob} />
    </div>
  );
}

function KpiCard({ testid, icon, label, value, accent }) {
  return (
    <div className="bg-[#12141D] border border-border p-4 relative overflow-hidden" data-testid={testid}>
      <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <span style={{ color: accent }}>{icon}</span> {label}
      </div>
      <div className="font-heading font-bold text-3xl tabular-nums mt-2">{value}</div>
    </div>
  );
}

function SummaryCard({ testid, title, entries, colorMap }) {
  return (
    <div className="bg-[#12141D] border border-border p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">{title}</div>
      <div className="space-y-2">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colorMap[k] || "#94A3B8" }} />
              <span className="text-white/80">{k}</span>
            </span>
            <span className="font-mono tabular-nums">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
